import { useState, useCallback, useEffect } from 'react';
import { Loader } from '@mantine/core';
import { IconPlus, IconCheck } from '@tabler/icons-react';
import { v4 as uuidv4 } from 'uuid';
import { useSectionCompletion } from '../hooks/useSectionCompletion';
import { useMedicalHistory } from '../hooks/useMedicalHistory';
import { useUnsavedChangesGuard } from '../hooks/useUnsavedChangesGuard';
import { showSuccessToast, showErrorToast } from '../utils/notifications';
import { ActiveConditionForm } from '../components/medicalHistory/ActiveConditionForm';
import { PriorEventForm } from '../components/medicalHistory/PriorEventForm';
import { ProcedureForm } from '../components/medicalHistory/ProcedureForm';
import type {
  ActiveConditionLocal,
  PriorEventLocal,
  ProcedureLocal,
  MedicalHistoryData,
} from '../types/medicalHistory';

// Extended types to track "new" state
interface ActiveConditionWithNew extends ActiveConditionLocal {
  isNewItem?: boolean;
}

interface PriorEventWithNew extends PriorEventLocal {
  isNewItem?: boolean;
}

interface ProcedureWithNew extends ProcedureLocal {
  isNewItem?: boolean;
}

// Create empty items
function createEmptyCondition(): ActiveConditionWithNew {
  return {
    localId: uuidv4(),
    conditionName: '',
    onsetDate: null,
    isNewItem: true,
  };
}

function createEmptyPriorEvent(): PriorEventWithNew {
  return {
    localId: uuidv4(),
    eventName: '',
    eventDate: null,
    status: 'resolved',
    isNewItem: true,
  };
}

function createEmptyProcedure(): ProcedureWithNew {
  return {
    localId: uuidv4(),
    procedureName: '',
    procedureDate: null,
    isNewItem: true,
  };
}

export function MedicalArchitecture() {
  const { returnToDashboard } = useSectionCompletion('medical-architecture');
  const {
    medicalHistoryData,
    isLoading,
    isSaving,
    error,
    hasActiveConditions,
    hasPriorEvents,
    hasProcedures,
    reviewedFlags,
    setReviewedFlag,
    saveAll,
    availableConditions,
  } = useMedicalHistory();

  // Local form state
  const [activeConditions, setActiveConditions] = useState<ActiveConditionWithNew[]>([]);
  const [priorEvents, setPriorEvents] = useState<PriorEventWithNew[]>([]);
  const [procedures, setProcedures] = useState<ProcedureWithNew[]>([]);

  // Sync state when data loads
  useEffect(() => {
    if (!isLoading) {
      // Items from backend are not new
      setActiveConditions(
        medicalHistoryData.activeConditions.map((c) => ({ ...c, isNewItem: false }))
      );
      setPriorEvents(
        medicalHistoryData.priorEvents.map((e) => ({ ...e, isNewItem: false }))
      );
      setProcedures(
        medicalHistoryData.procedures.map((p) => ({ ...p, isNewItem: false }))
      );
    }
  }, [isLoading, medicalHistoryData]);

  // Active Conditions handlers
  const handleAddCondition = useCallback(() => {
    setActiveConditions((prev) => [...prev, createEmptyCondition()]);
  }, []);

  const handleUpdateCondition = useCallback(
    (localId: string, updated: ActiveConditionWithNew) => {
      setActiveConditions((prev) =>
        prev.map((c) => (c.localId === localId ? updated : c))
      );
    },
    []
  );

  const handleRemoveCondition = useCallback((localId: string) => {
    setActiveConditions((prev) => {
      const item = prev.find((c) => c.localId === localId);
      // If it's a new item that was never saved, just remove it entirely
      if (item?.isNewItem) {
        return prev.filter((c) => c.localId !== localId);
      }
      // Otherwise mark as deleted
      return prev.map((c) =>
        c.localId === localId ? { ...c, isDeleted: true } : c
      );
    });
  }, []);

  const handleSubmitCondition = useCallback((localId: string) => {
    setActiveConditions((prev) =>
      prev.map((c) =>
        c.localId === localId ? { ...c, isNewItem: false } : c
      )
    );
  }, []);

  // Prior Events handlers
  const handleAddPriorEvent = useCallback(() => {
    setPriorEvents((prev) => [...prev, createEmptyPriorEvent()]);
  }, []);

  const handleUpdatePriorEvent = useCallback(
    (localId: string, updated: PriorEventWithNew) => {
      setPriorEvents((prev) =>
        prev.map((e) => (e.localId === localId ? updated : e))
      );
    },
    []
  );

  const handleRemovePriorEvent = useCallback((localId: string) => {
    setPriorEvents((prev) => {
      const item = prev.find((e) => e.localId === localId);
      if (item?.isNewItem) {
        return prev.filter((e) => e.localId !== localId);
      }
      return prev.map((e) =>
        e.localId === localId ? { ...e, isDeleted: true } : e
      );
    });
  }, []);

  const handleSubmitPriorEvent = useCallback((localId: string) => {
    setPriorEvents((prev) =>
      prev.map((e) =>
        e.localId === localId ? { ...e, isNewItem: false } : e
      )
    );
  }, []);

  // Procedures handlers
  const handleAddProcedure = useCallback(() => {
    setProcedures((prev) => [...prev, createEmptyProcedure()]);
  }, []);

  const handleUpdateProcedure = useCallback(
    (localId: string, updated: ProcedureWithNew) => {
      setProcedures((prev) =>
        prev.map((p) => (p.localId === localId ? updated : p))
      );
    },
    []
  );

  const handleRemoveProcedure = useCallback((localId: string) => {
    setProcedures((prev) => {
      const item = prev.find((p) => p.localId === localId);
      if (item?.isNewItem) {
        return prev.filter((p) => p.localId !== localId);
      }
      return prev.map((p) =>
        p.localId === localId ? { ...p, isDeleted: true } : p
      );
    });
  }, []);

  const handleSubmitProcedure = useCallback((localId: string) => {
    setProcedures((prev) =>
      prev.map((p) =>
        p.localId === localId ? { ...p, isNewItem: false } : p
      )
    );
  }, []);

  // Single save handler — saves data and navigates to dashboard
  const handleSave = async () => {
    const data: MedicalHistoryData = {
      activeConditions: activeConditions.filter((c) => !c.isNewItem),
      priorEvents: priorEvents.filter((e) => !e.isNewItem),
      procedures: procedures.filter((p) => !p.isNewItem),
    };

    const success = await saveAll(data);
    if (success) {
      showSuccessToast('Medical history saved');
      returnToDashboard();
    } else {
      showErrorToast('Unable to save. Please try again.');
    }
  };

  // Toggle "no history" flags
  const handleToggleNoConditions = () => {
    setReviewedFlag('noActiveConditions', !reviewedFlags.noActiveConditions);
  };

  const handleToggleNoPriorEvents = () => {
    setReviewedFlag('noPriorEvents', !reviewedFlags.noPriorEvents);
  };

  const handleToggleNoProcedures = () => {
    setReviewedFlag('noProcedures', !reviewedFlags.noProcedures);
  };

  // Filter visible items for display
  const visibleConditions = activeConditions.filter((c) => !c.isDeleted);
  const visiblePriorEvents = priorEvents.filter((e) => !e.isDeleted);
  const visibleProcedures = procedures.filter((p) => !p.isDeleted);

  // Check if there are any unsaved new items
  const hasUnsavedItems =
    activeConditions.some((c) => c.isNewItem && !c.isDeleted) ||
    priorEvents.some((e) => e.isNewItem && !e.isDeleted) ||
    procedures.some((p) => p.isNewItem && !p.isDeleted);

  // Navigation guard for unsaved items
  useUnsavedChangesGuard(hasUnsavedItems);

  if (isLoading) {
    return (
      <div className="animate-fade-in flex items-center justify-center py-12">
        <Loader size="lg" color="cyan" />
      </div>
    );
  }

  return (
    <div className="animate-fade-in">
      <p className="text-gray-400 mb-8">
        Build a comprehensive picture of your health history. Include active
        conditions, past illnesses, and any surgeries or procedures.
      </p>

      {error && (
        <div className="mb-6 p-4 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm">
          {error}
        </div>
      )}

      <div className="space-y-10">
        {/* Section 1: Active Conditions */}
        <section>
          <h3 className="text-lg font-semibold text-gray-200 mb-4 flex items-center gap-2">
            <span className="w-6 h-6 rounded-full bg-cyan-500/20 text-cyan-400 text-sm flex items-center justify-center">
              1
            </span>
            Current Medical Conditions
            <span className="text-xs text-gray-500 font-normal">(Optional)</span>
          </h3>

          <div className="pl-8 space-y-4">
            {visibleConditions.length === 0 && !hasActiveConditions ? (
              <div className="p-4 border border-dashed border-gray-700 rounded-lg text-center text-gray-500">
                <p>No active conditions added yet</p>
                <p className="text-xs mt-1">
                  Add any ongoing health conditions you are currently managing
                </p>
              </div>
            ) : (
              visibleConditions.map((condition) => (
                <ActiveConditionForm
                  key={condition.localId}
                  condition={condition}
                  onChange={(updated) =>
                    handleUpdateCondition(condition.localId, updated)
                  }
                  onRemove={() => handleRemoveCondition(condition.localId)}
                  onSubmit={() => handleSubmitCondition(condition.localId)}
                  isNew={condition.isNewItem}
                  disabled={isSaving}
                />
              ))
            )}

            <div className="flex gap-2">
              <button
                type="button"
                onClick={handleAddCondition}
                disabled={isSaving || reviewedFlags.noActiveConditions}
                className="flex-1 p-3 border border-dashed border-gray-700 rounded-lg text-gray-400 hover:border-cyan-500/50 hover:text-cyan-400 transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
              >
                <IconPlus size={16} />
                Add Current Condition
              </button>
              {!hasActiveConditions && (
                <button
                  type="button"
                  onClick={handleToggleNoConditions}
                  disabled={isSaving}
                  className={`px-4 py-3 rounded-lg transition-colors flex items-center gap-2 ${
                    reviewedFlags.noActiveConditions
                      ? 'bg-green-500/20 border border-green-500/50 text-green-400'
                      : 'border border-dashed border-gray-700 text-gray-400 hover:border-green-500/50 hover:text-green-400'
                  }`}
                >
                  <IconCheck size={16} />
                  None to add
                </button>
              )}
            </div>
          </div>
        </section>

        {/* Section 2: Prior Illnesses & Events */}
        <section>
          <h3 className="text-lg font-semibold text-gray-200 mb-4 flex items-center gap-2">
            <span className="w-6 h-6 rounded-full bg-cyan-500/20 text-cyan-400 text-sm flex items-center justify-center">
              2
            </span>
            Past Illnesses & Significant Events
            <span className="text-xs text-gray-500 font-normal">(Optional)</span>
          </h3>

          <div className="pl-8 space-y-4">
            {visiblePriorEvents.length === 0 && !hasPriorEvents ? (
              <div className="p-4 border border-dashed border-gray-700 rounded-lg text-center text-gray-500">
                <p>No past illnesses or events added yet</p>
                <p className="text-xs mt-1">
                  Include resolved conditions, hospitalizations, or significant
                  health events
                </p>
              </div>
            ) : (
              visiblePriorEvents.map((event) => (
                <PriorEventForm
                  key={event.localId}
                  event={event}
                  onChange={(updated) =>
                    handleUpdatePriorEvent(event.localId, updated)
                  }
                  onRemove={() => handleRemovePriorEvent(event.localId)}
                  onSubmit={() => handleSubmitPriorEvent(event.localId)}
                  isNew={event.isNewItem}
                  disabled={isSaving}
                />
              ))
            )}

            <div className="flex gap-2">
              <button
                type="button"
                onClick={handleAddPriorEvent}
                disabled={isSaving || reviewedFlags.noPriorEvents}
                className="flex-1 p-3 border border-dashed border-gray-700 rounded-lg text-gray-400 hover:border-cyan-500/50 hover:text-cyan-400 transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
              >
                <IconPlus size={16} />
                Add Past Illness or Event
              </button>
              {!hasPriorEvents && (
                <button
                  type="button"
                  onClick={handleToggleNoPriorEvents}
                  disabled={isSaving}
                  className={`px-4 py-3 rounded-lg transition-colors flex items-center gap-2 ${
                    reviewedFlags.noPriorEvents
                      ? 'bg-green-500/20 border border-green-500/50 text-green-400'
                      : 'border border-dashed border-gray-700 text-gray-400 hover:border-green-500/50 hover:text-green-400'
                  }`}
                >
                  <IconCheck size={16} />
                  None to add
                </button>
              )}
            </div>
          </div>
        </section>

        {/* Section 3: Surgical & Procedural History */}
        <section>
          <h3 className="text-lg font-semibold text-gray-200 mb-4 flex items-center gap-2">
            <span className="w-6 h-6 rounded-full bg-cyan-500/20 text-cyan-400 text-sm flex items-center justify-center">
              3
            </span>
            Surgical & Procedural History
            <span className="text-xs text-gray-500 font-normal">(Optional)</span>
          </h3>

          <div className="pl-8 space-y-4">
            {visibleProcedures.length === 0 && !hasProcedures ? (
              <div className="p-4 border border-dashed border-gray-700 rounded-lg text-center text-gray-500">
                <p>No surgeries or procedures added yet</p>
                <p className="text-xs mt-1">
                  Include any surgeries, biopsies, or medical procedures you've
                  had
                </p>
              </div>
            ) : (
              visibleProcedures.map((procedure) => (
                <ProcedureForm
                  key={procedure.localId}
                  procedure={procedure}
                  onChange={(updated) =>
                    handleUpdateProcedure(procedure.localId, updated)
                  }
                  onRemove={() => handleRemoveProcedure(procedure.localId)}
                  onSubmit={() => handleSubmitProcedure(procedure.localId)}
                  availableConditions={availableConditions}
                  isNew={procedure.isNewItem}
                  disabled={isSaving}
                />
              ))
            )}

            <div className="flex gap-2">
              <button
                type="button"
                onClick={handleAddProcedure}
                disabled={isSaving || reviewedFlags.noProcedures}
                className="flex-1 p-3 border border-dashed border-gray-700 rounded-lg text-gray-400 hover:border-cyan-500/50 hover:text-cyan-400 transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
              >
                <IconPlus size={16} />
                Add Surgery or Procedure
              </button>
              {!hasProcedures && (
                <button
                  type="button"
                  onClick={handleToggleNoProcedures}
                  disabled={isSaving}
                  className={`px-4 py-3 rounded-lg transition-colors flex items-center gap-2 ${
                    reviewedFlags.noProcedures
                      ? 'bg-green-500/20 border border-green-500/50 text-green-400'
                      : 'border border-dashed border-gray-700 text-gray-400 hover:border-green-500/50 hover:text-green-400'
                  }`}
                >
                  <IconCheck size={16} />
                  None to add
                </button>
              )}
            </div>
          </div>
        </section>
      </div>

      {/* Action Buttons */}
      <div className="mt-10 pt-6 border-t border-gray-800 flex items-center justify-between">
        <div>
          {hasUnsavedItems && (
            <p className="text-sm text-gray-500">
              Complete or cancel open forms before saving
            </p>
          )}
        </div>
        <button
          onClick={handleSave}
          disabled={isSaving || hasUnsavedItems}
          className="btn disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isSaving ? (
            <span className="flex items-center gap-2">
              <Loader size={16} color="white" />
              Saving...
            </span>
          ) : (
            'Save'
          )}
        </button>
      </div>
    </div>
  );
}
